import { refreshTokenApi } from "@/api/services/main/auth";
import { getCustomSentryExceptionError, handleApiErrorWithCode, isApiError, isNetworkError } from "@/utils/error";
import { removeToken, verifyToken } from "@/utils/token";
import { getMatchRoutes, getQueryFromURL } from "@/utils/url";
import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";
import { AppRoutes } from "@/routes/config";
import { getAppStoreUrl } from "@/utils/app-store";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { PRIVACY_CONSENT_APPROVED_KEY } from "@/constants/common";
import i18n from "@/i18n/config";
import { toast } from "@/utils/toast";
import { isNativeWebView } from "@/hooks/useNativeBridge";
import { attachAppSignature } from "@/utils/app-signature";

if (!process.env.REACT_APP_API_HOST) {
  throw new Error("please provide REACT_APP_API_HOST in env");
}

export const baseURL = `${process.env.REACT_APP_API_HOST}/app`;

export const globalBaseURL = `${process.env.REACT_APP_API_HOST}/global`;

export const zipCodeURL = process.env.REACT_APP_API_ZIP_CODE ?? "";

export const globalApi = axios.create({
  baseURL: globalBaseURL,
});

export const zipCodeApi = axios.create({
  baseURL: zipCodeURL,
});

export const clientApi = axios.create({
  baseURL,
});

// Clean axios instance for refresh token calls — no auth header, no interceptors
export const refreshClient = axios.create({
  baseURL,
});

// If successful (do nothing)
export const onSuccess = (response: AxiosResponse) => {
  Sentry.endSession();
  return response;
};

let isRefreshing = false;
let hasRedirectedToLogout = false;
const STORE_REDIRECT_RELEASE_MS = 1000;
const APP_UPDATE_REDIRECT_DELAY_MS = 3000;
const WEB_ACCESS_REDIRECT_DELAY_MS = 3000;
let hasRedirectedToStore = false;
let refreshQueue: { resolve: (token: string) => void; reject: (error: unknown) => void }[] = [];

/** 403 error codes that require logout (account suspended, canceled, or expired) */
const LOGOUT_ERROR_CODES = [
  ERROR_STATUS_CODES.LEARNER_ACCOUNT_SUSPENDED,
  ERROR_STATUS_CODES.COMPANY_CONTRACT_EXPIRED,
  ERROR_STATUS_CODES.MATCH_ACCOUNT_REGISTRATION_CANCELED,
  ERROR_STATUS_CODES.MATCH_ACCOUNT_REGISTRATION_TRIAL_CANCELED,
  ERROR_STATUS_CODES.SUPPORT_USAGE_EXPIRED,
];

// Auth endpoints should not trigger 401 refresh or global error redirects
const isAuthRequest = (config?: AxiosRequestConfig) => {
  const url = config?.url ?? "";
  return url === "/login" || url === "/refresh-token";
};

const isWebAccessNotAllowed = (error: AxiosError) =>
  error.response?.status === 403 && isApiError(error.response.data) && error.response.data.code === ERROR_STATUS_CODES.WEB_ACCESS_NOT_ALLOWED;

// In the app this 403 means "update the app", not an auth failure: keep the session. On the web it logs out as usual.
export const isAppUpdateRequiredOnRefresh = (error: unknown): error is AxiosError =>
  isNativeWebView() && axios.isAxiosError(error) && isWebAccessNotAllowed(error);

/**
 * Replays a request with the refreshed token. Plain `axios` skips our interceptors, so the app signature must be renewed here.
 */
const retryWithToken = async (originalConfig: InternalAxiosRequestConfig, newToken: string) => {
  const config = await attachAppSignature({
    ...originalConfig,
    headers: AxiosHeaders.from(originalConfig.headers).set("Authorization", `Bearer ${newToken}`),
  });
  return axios(config);
};

// Goes to the store, not home: home would be rejected the same way.
export const redirectToStoreForWebAccess = () => {
  // One redirect per burst; released after navigating since the page may survive it
  if (hasRedirectedToStore) return;
  hasRedirectedToStore = true;

  // App: too old to sign requests. Web: the organization blocks web access.
  const isApp = isNativeWebView();
  const delayMs = isApp ? APP_UPDATE_REDIRECT_DELAY_MS : WEB_ACCESS_REDIRECT_DELAY_MS;
  const message = isApp ? i18n.t("common.appUpdateRequired") : i18n.t(`common.apiErrors.${ERROR_STATUS_CODES.WEB_ACCESS_NOT_ALLOWED}`);
  toast.error(message, { autoClose: delayMs });
  setTimeout(() => {
    window.location.replace(getAppStoreUrl());
    setTimeout(() => {
      hasRedirectedToStore = false;
    }, STORE_REDIRECT_RELEASE_MS);
  }, delayMs);
};

export const onError = async (error: AxiosError) => {
  // Before the auth-request shortcut so login/refresh are covered too.
  if (isWebAccessNotAllowed(error)) {
    redirectToStoreForWebAccess();
    return Promise.reject(error.response?.data);
  }

  // For auth requests (login, refresh), skip global handlers and let the caller handle errors
  if (isAuthRequest(error.config)) {
    return Promise.reject(error.response?.data ?? error);
  }

  if (error.response) {
    const query = getQueryFromURL<{ token: string }>();
    const token = query?.token;
    // External token in URL — redirect to 404 instead of refresh
    if (error.response.status === 401 && token) {
      location.href = AppRoutes.pageNotFound;
      return;
    }
    if (error.response.status === 401 && error.config) {
      const originalConfig = error.config;
      Cookies.remove("learner-token", { path: "/" });

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshToken = Cookies.get("learner-refresh-token");
          const accountId = Cookies.get("learner-account-id");
          if (!accountId || !refreshToken) {
            return handleRefreshFailure(error);
          }
          await refreshTokenApi({
            refreshToken,
            accountId,
          });

          const newToken = Cookies.get("learner-token");
          if (!newToken) {
            // Reject queued requests — no valid token
            return handleRefreshFailure(error);
          }

          // Resolve all queued requests with the new token
          refreshQueue.forEach(({ resolve: resolveQueued }) => resolveQueued(newToken));
          refreshQueue = [];

          return retryWithToken(originalConfig, newToken).catch((retryError) => {
            if (retryError?.response?.status === 401) return handleRefreshFailure(retryError);
            return Promise.reject(retryError);
          });
        } catch (refreshError) {
          // Reject all queued requests on refresh failure
          return handleRefreshFailure(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Another refresh is in-flight — queue this request
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (newToken: string) => {
            resolve(
              retryWithToken(originalConfig, newToken).catch((retryError) => {
                if (retryError?.response?.status === 401) return handleRefreshFailure(retryError);
                return Promise.reject(retryError);
              }),
            );
          },
          reject,
        });
      });
    }

    if (error.response.status === 403) {
      if (isApiError(error.response.data)) {
        const { code } = error.response.data;
        // Trial restriction — redirect to study page
        if (code === ERROR_STATUS_CODES.TRIAL_SUBJECT_RESTRICTED || code === ERROR_STATUS_CODES.TRIAL_VOCABULARY_RESTRICTED) {
          if (window.location.pathname !== AppRoutes.study) {
            window.location.replace(AppRoutes.study);
          }
          return Promise.reject(error.response.data);
        }
        // Account suspended, canceled, or expired — logout
        if (LOGOUT_ERROR_CODES.includes(code)) {
          redirectToLogout();
          return;
        }
      }
      // Other 403 errors — redirect to home without logout
      if (window.location.pathname !== AppRoutes.home) {
        window.location.replace(AppRoutes.home);
      }
      return Promise.reject(error.response?.data);
    }
  }
  handleApiErrorWithCode(error);
  if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
    Sentry.captureException(getCustomSentryExceptionError(error));
  }
  Sentry.endSession();
  return Promise.reject(error.response?.data);
};

clientApi.interceptors.request.use(
  (config) => {
    // Always inject latest token from cookies to avoid race condition on page reload
    // (setAxiosAuth in withAuth's useEffect may not have fired yet)
    const token = Cookies.get("learner-token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const matchRoutes = getMatchRoutes();
    config.headers["X-RouteName"] = matchRoutes.at(-1)?.id;

    const screenRouteUrl = new URL(window.location.href);
    screenRouteUrl.search = "";
    config.headers["X-RouteURL"] = screenRouteUrl;

    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

globalApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

clientApi.interceptors.request.use(attachAppSignature);
globalApi.interceptors.request.use(attachAppSignature);
refreshClient.interceptors.request.use(attachAppSignature);

clientApi.interceptors.response.use(onSuccess, onError);
globalApi.interceptors.response.use(onSuccess, onError);

export const setAxiosAuth = () => {
  const token = Cookies.get("learner-token");
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const setExternalAxiosAuth = () => {
  const query = getQueryFromURL<{ token: string }>();
  const token = query?.token;
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const removeAxiosAuth = () => {
  clientApi.defaults.headers.common.Authorization = "";
  globalApi.defaults.headers.common.Authorization = "";
};

export const isAuth = () => {
  if (typeof window !== "undefined") {
    const token = Cookies.get("learner-token");
    const isAuthenticated = !!token && verifyToken(token);
    return isAuthenticated;
  }
  return false;
};

// Build login URL with current accountId (learnerId) and optional redirectUrl as query params
export const getLoginUrl = (redirectUrl?: string | null, learnerId?: string | null) => {
  const accountId = learnerId || Cookies.get("learner-account-id");
  const params = new URLSearchParams();
  if (accountId) params.set("learnerId", accountId);
  if (redirectUrl) params.set("redirectUrl", redirectUrl);
  const qs = params.toString();
  return qs ? `${AppRoutes.login}?${qs}` : AppRoutes.login;
};

// Build logout URL with optional redirectUrl so users return to the same page after re-login
export const getLogoutUrl = (query?: { redirectUrl?: string; learnerId?: string }) => {
  const { redirectUrl, learnerId } = query || {};
  const searchParams = new URLSearchParams();
  if (redirectUrl) searchParams.set("redirectUrl", redirectUrl);
  if (learnerId) searchParams.set("learnerId", learnerId);
  const qs = searchParams.toString();
  return qs ? `${AppRoutes.logout}?${qs}` : AppRoutes.logout;
};

// Redirect to /logout with current page as redirectUrl (skip if already on /logout to prevent loops)
const redirectToLogout = () => {
  if (hasRedirectedToLogout || window.location.pathname === AppRoutes.logout) return;
  hasRedirectedToLogout = true;
  const currentUrl = `${location.pathname}${location.search}`;
  location.href = getLogoutUrl({ redirectUrl: currentUrl });
};

export const logout = () => {
  removeToken();
  Cookies.remove("learner-user-id", { path: "/" });
  Cookies.remove("learner-account-id", { path: "/" });
  Cookies.remove("learner-id", { path: "/" });
  Cookies.remove("learner-full-name", { path: "/" });
  Cookies.remove("learner-industry-id", { path: "/" });
  sessionStorage.clear();
  localStorage.removeItem(PRIVACY_CONSENT_APPROVED_KEY);
};

export const lazyApi = <T = void>(fn: () => Promise<T>): [AbortController, () => Promise<T>] => {
  const controller = new AbortController();

  return [controller, fn];
};

// Helper to clear refresh queue and redirect to logout
const handleRefreshFailure = (error: unknown) => {
  if (isAppUpdateRequiredOnRefresh(error)) {
    refreshQueue.forEach(({ reject: rejectQueued }) => rejectQueued(error.response?.data));
    refreshQueue = [];
    redirectToStoreForWebAccess();
    return Promise.reject(error.response?.data);
  }
  if (refreshQueue.length > 0) {
    refreshQueue.forEach(({ reject: rejectQueued }) => rejectQueued(error));
    refreshQueue = [];
  }
  // Only force logout when the SERVER rejected the refresh (a real auth failure). A transport-level
  // failure (no response — typically right after the app resumes from background) must NOT end a
  // valid session; reject and let a later attempt refresh when the network is back.
  if (isNetworkError(error)) {
    return Promise.reject(error);
  }
  redirectToLogout();
  return Promise.reject(error);
};
